import React from "react";
import { Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, Select, Input, Tooltip, Badge, } from "@chakra-ui/react";
import { FaDownload, FaEnvelope } from "react-icons/fa";
import { RiRefund2Line, RiHistoryLine, RiFileList3Line } from "react-icons/ri";
import { InitiateRefundModal } from "./components/InitiateRefundModal";
import { RefundDetailsModal } from "./components/RefundDetailsModal";
import { useInvoice } from "./Invoice.hook";
import { Layout } from "../../GlobalComponent/Layout/Layout";

export const Invoice = () => {
    const {
        allInvoices, page, limit, setLimit, setPage, filters, setFilters, orderTypes, paymentStatuses, isLoading, generateInvoice, sendInvoiceOnEmail, ticketIdInput, setTicketIdInput, searchByTicketId, clearTicketIdSearch, getAllInvoicesForDownloadAction,
        selectedInvoiceForRefund, isRefundModalOpen, openInitiateRefundModal, closeInitiateRefundModal, handleInitiateRefund, isRefundLoading,
        selectedTicketForRefundDetails, refundDetailsList, isRefundDetailsModalOpen, setIsRefundDetailsModalOpen, openRefundDetailsModal, isRefundDetailsLoading, isSyncingRefund, handleSyncRefund, generateRefundCreditNote, downloadRefundInvoiceByTicket
    } = useInvoice();
    const [isDownloading, setIsDownloading] = React.useState(false);
    function getCookie(name: string) {
        const cookies = document.cookie.split('; ');
        for (let cookie of cookies) {
            const [key, value] = cookie.split('=');
            if (key === name && value === "true") {
                return true;
            }
        }
        return false;
    }

    const isSuperAdmin = getCookie("isSuperAdmin");
    const getPaymentStatusBadge = (status: string) => {
        switch (status) {
            case "paid":
                return <Badge colorScheme="green">PAID</Badge>;
            case "refund_initiated":
                return <Badge colorScheme="orange">REFUND INITIATED</Badge>;
            case "partially_refunded":
                return <Badge colorScheme="purple">PARTIALLY REFUNDED</Badge>;
            case "refunded":
                return <Badge colorScheme="purple">REFUNDED</Badge>;
            case "failed":
                return <Badge colorScheme="red">FAILED</Badge>;
            case "pending":
            case "created":
                return <Badge colorScheme="yellow">{status.toUpperCase()}</Badge>;
            default:
                return <Badge colorScheme="gray">{status?.toUpperCase() || "N/A"}</Badge>;
        }
    };


    return (
        <Layout>
            <Box p={6}>
                <Text fontSize="2xl" textAlign={"center"} fontWeight="bold" mb={4}>Invoices</Text>
                {/* Filters */}
                <Flex mb={4} gap={4} align="center" wrap="wrap">
                    {/* TICKET ID SEARCH */}
                    <Input
                        w="170px"
                        placeholder="Search by Ticket ID"
                        value={ticketIdInput}
                        onChange={(e) => setTicketIdInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && searchByTicketId()}
                    />

                    {/* TYPE FILTER */}
                    <Select
                        w="180px"
                        placeholder="Select type"
                        value={filters.type}
                        onChange={(e) => {
                            setFilters((prev) => ({ ...prev, type: e.target.value }));
                            setPage(1);
                        }}
                    >
                        {orderTypes?.length > 0 && orderTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </Select>

                    {/* PAYMENT STATUS FILTER */}
                    <Select
                        w="160px"
                        placeholder="Select Payment Status"
                        value={filters.paymentStatus}
                        onChange={(e) => {
                            setFilters((prev) => ({ ...prev, paymentStatus: e.target.value }));
                            setPage(1);
                        }}
                    >
                        {paymentStatuses?.length > 0 && paymentStatuses.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </Select>
                    {/* DATE RANGE FILTER */}
                    <Flex
                        align="center"
                        gap={2}
                        px={3}
                        py={2}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="white"
                    >
                        <Text fontSize="sm" color="gray.600">
                            Date
                        </Text>

                        <Input
                            size="sm"
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => {
                                setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                                setPage(1);
                            }}
                        />

                        <Text fontSize="sm" color="gray.500">
                            →
                        </Text>

                        <Input
                            size="sm"
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => {
                                setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                                setPage(1);
                            }}
                        />
                    </Flex>

                    <Button colorScheme="blue" onClick={searchByTicketId} isDisabled={isLoading}>Search</Button>
                    {filters.ticketId && <Button variant="outline" onClick={clearTicketIdSearch}>Clear</Button>}

                    <Tooltip label={!isSuperAdmin ? "Admin access required" : "Download all invoices as ZIP"}>
                        <Button
                            leftIcon={<FaDownload />}
                            colorScheme="blue"
                            isLoading={isDownloading}
                            loadingText="Preparing ZIP..."
                            onClick={async () => {
                                setIsDownloading(true);
                                try {
                                    const res = await getAllInvoicesForDownloadAction({
                                        page,
                                        limit,
                                        type: filters.type,
                                        paymentStatus: filters.paymentStatus,
                                        startDate: filters.startDate,
                                        endDate: filters.endDate,
                                        ticketId: filters.ticketId,
                                    });
                                    if (!res.status) {
                                        alert(res.message || "Unauthorized. Super admin access required.");
                                        return;
                                    }
                                    const blob = new Blob([res.data?.data ?? res.data], { type: 'application/zip' });
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = `invoices-${new Date().toISOString().split('T')[0]}.zip`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                } finally {
                                    setIsDownloading(false);
                                }
                            }}
                            isDisabled={!isSuperAdmin || isLoading || !allInvoices?.data?.some((inv: any) => inv.paymentStatus === "paid")}
                        >
                            Download All
                        </Button>
                    </Tooltip>

                </Flex>

                <Box borderWidth={1} borderRadius="lg" overflowX="auto">
                    <Table variant="simple">
                        <Thead bg="gray.100">
                            <Tr cursor={"auto"}>
                                <Th>SrNo</Th>
                                <Th>TicketId</Th>
                                <Th>subscriberName</Th>
                                <Th>Type</Th>
                                <Th>Company</Th>
                                <Th isNumeric>Amount</Th>
                                <Th >paymentStatus</Th>
                                <Th>Date</Th>
                                <Th>Action</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {allInvoices?.data && allInvoices?.data.length > 0 ? (
                                allInvoices.data.map((invoice: any, ind) => (
                                    <Tr key={invoice.id} cursor={"auto"} >
                                        <Td>{(page - 1) * limit + ind + 1}</Td>
                                        <Td>{invoice?.ticketId || "N/A"}</Td>
                                        <Td color={invoice?.subscriberData ? "none" : "red"}>{invoice?.subscriberData?.name || "subscriberNotFound"}</Td>
                                        <Td>{invoice.orderFor}</Td>
                                        <Td>{invoice?.subscriberData?.company?.name || "Not Present"}</Td>
                                        <Td isNumeric>
                                            <Text>{invoice.amount}</Text>
                                            {invoice?.refundedAmount > 0 && (
                                                <Text fontSize="xs" color="red.500" fontWeight="medium">
                                                    Refund: ₹{invoice.refundedAmount}
                                                </Text>
                                            )}
                                        </Td>
                                        <Td>{getPaymentStatusBadge(invoice.paymentStatus)}</Td>
                                        <Td>{new Date(invoice.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</Td>
                                        <Td>
                                            <Flex gap={2}>
                                                {(invoice.paymentStatus === "paid" || invoice.paymentStatus === "refunded" || invoice.paymentStatus === "partially_refunded" || invoice.paymentStatus === "refund_initiated") && (
                                                    <Tooltip label="Download Invoice">
                                                        <IconButton
                                                            onClick={() => { generateInvoice({ invoiceId: invoice._id }) }}
                                                            aria-label="Download Invoice"
                                                            icon={<FaDownload />}
                                                            colorScheme="teal"
                                                            variant="outline"
                                                            isDisabled={isLoading}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {(invoice.paymentStatus === "paid" || invoice.paymentStatus === "partially_refunded") && (
                                                    <>
                                                        <Tooltip label="Send Invoice on Email">
                                                            <IconButton
                                                                onClick={() => { sendInvoiceOnEmail({ invoiceId: invoice._id, email: invoice?.subscriberData?.email }) }}
                                                                aria-label="Send Invoice on Email"
                                                                icon={<FaEnvelope />}
                                                                colorScheme="purple"
                                                                variant="outline"
                                                                isDisabled={isLoading || !invoice?.subscriberData?.email}
                                                            />
                                                        </Tooltip>
                                                        {invoice.orderFor?.toLowerCase() === "ambulance" && invoice.paymentStatus === "paid" && (
                                                            <Tooltip label="Initiate Refund">
                                                                <IconButton
                                                                    onClick={() => openInitiateRefundModal(invoice)}
                                                                    aria-label="Initiate Refund"
                                                                    icon={<RiRefund2Line />}
                                                                    colorScheme="red"
                                                                    variant="outline"
                                                                    isDisabled={isLoading}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                )}
                                                {(invoice.paymentStatus === "refunded" || invoice.paymentStatus === "partially_refunded") && invoice.orderFor?.toLowerCase() === "ambulance" && (
                                                    <Tooltip label="Download Refund Credit Note">
                                                        <IconButton
                                                            onClick={() => downloadRefundInvoiceByTicket(invoice.ticketId)}
                                                            aria-label="Download Refund Credit Note"
                                                            icon={<RiFileList3Line />}
                                                            colorScheme="purple"
                                                            variant="outline"
                                                            isDisabled={isLoading || !invoice.ticketId}
                                                        />
                                                    </Tooltip>
                                                )}
                                                {(invoice.paymentStatus === "refund_initiated" || invoice.paymentStatus === "refunded" || invoice.paymentStatus === "partially_refunded") && invoice.orderFor?.toLowerCase() === "ambulance" && (
                                                    <Tooltip label="View Refund Details / Sync">
                                                        <IconButton
                                                            onClick={() => openRefundDetailsModal(invoice.ticketId)}
                                                            aria-label="View Refund Status"
                                                            icon={<RiHistoryLine />}
                                                            colorScheme="orange"
                                                            variant="outline"
                                                            isDisabled={isLoading || !invoice.ticketId}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Flex>
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={6} textAlign="center">Loading...</Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>

                {/* Pagination Controls */}
                <Flex mt={4} gap={4} align="center" justify="center">
                    <Button
                        onClick={() => setPage((prev) => prev - 1)}
                        isDisabled={!allInvoices?.hasPreviousPage || isLoading}
                    >
                        Previous
                    </Button>

                    <Select w="120px" value={page} onChange={(e) => setPage(Number(e.target.value))}>
                        {Array.from({ length: allInvoices?.totalPages || 0 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                                Page {i + 1}
                            </option>
                        ))}
                    </Select>

                    <Button
                        onClick={() => setPage((prev) => prev + 1)}
                        isDisabled={!allInvoices?.hasNextPage || isLoading}
                    >
                        Next
                    </Button>

                    <Select
                        w="100px"
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1);
                        }}
                    >
                        {[10, 20, 50, 100].map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </Select>
                </Flex>

                {/* Initiate Refund Modal */}
                <InitiateRefundModal
                    isOpen={isRefundModalOpen}
                    onClose={closeInitiateRefundModal}
                    invoice={selectedInvoiceForRefund}
                    onSubmit={handleInitiateRefund}
                    isLoading={isRefundLoading}
                />

                {/* Refund Details Modal */}
                <RefundDetailsModal
                    isOpen={isRefundDetailsModalOpen}
                    onClose={() => setIsRefundDetailsModalOpen(false)}
                    ticketId={selectedTicketForRefundDetails}
                    refunds={refundDetailsList}
                    isLoading={isRefundDetailsLoading}
                    isSyncing={isSyncingRefund}
                    onSync={handleSyncRefund}
                    onDownloadCreditNote={(refundId) => generateRefundCreditNote({ refundId })}
                />
            </Box>
        </Layout >
    );
}
